import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnInit,
  inject,
} from '@angular/core';
import { Platform } from '@ionic/angular/standalone';
import { Actions } from '@ngrx/effects';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { BiometricsActions } from './biometrics/store/biometrics.store';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CoreRoutes } from '../../core/routes/core.routes';
import { FilesRoutes } from '../../files/routes/files.routes';
import {
  sharedFilesAllSelector,
  ShareFileWithSdActions,
} from './files/store/share-file-with-sd.store';
import { AndroidShareSheetPluginService } from './files/services/android-share-sheet-plugin.service';
import { IosShareExtensionDataPluginService } from './files/services/ios-share-sheet-plugin.service';
import {
  StartInactivityTime,
  StopInactivityTime,
} from '../../auth/store/auth.actions';

@Component({
  selector: 'app-mobile-root',
  template: `
    <app-mobile-offline-banner />
    <ion-app>
      <ion-router-outlet />
    </ion-app>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    IosShareExtensionDataPluginService,
    AndroidShareSheetPluginService,
  ],
  standalone: false,
})
export class MobileAppComponent implements OnInit {
  private readonly platform = inject(Platform);
  private readonly actions = inject(Actions);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly iosShareExtensionDataPluginService = inject(
    IosShareExtensionDataPluginService
  );
  private readonly androidShareSheetPluginService = inject(
    AndroidShareSheetPluginService
  );
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  inactivityTimeStamp: number;
  androidShareAFileListener$ = this.androidShareSheetPluginService.watch().pipe(
    tap(androidShareData => {
      try {
        if (androidShareData?.length > 0) {
          this.store.dispatch(
            ShareFileWithSdActions.sharedFilesReceived({
              shareFiles: androidShareData,
            })
          );
          // NOTE: No cleanup here — UIMFlowStore.executeCheckOwnedAndSharedLogic
          // reads from Preferences on Android re-bootstrap and cleans up after dispatching
        }
      } catch (e) {
        console.error(e);
      }
    })
  );

  constructor() {
    this.androidShareAFileListener$.subscribe();
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        this.inactivityTimeStamp = Date.now();
        this.store.dispatch(new StopInactivityTime());

        this.store.dispatch(ShareFileWithSdActions.resetState());
        return;
      }

      // CASO 1: App estaba en background (inactivityTimeStamp existe)
      if (this.inactivityTimeStamp) {
        this.store.dispatch(new StartInactivityTime());
        const millisecondsPaused = Date.now() - this.inactivityTimeStamp;
        this.checkAndProcessSharedData();
        this.inactivityTimeStamp = null;
        if (environment.appPauseApp * 1000 < millisecondsPaused) {
          this.store.dispatch(BiometricsActions.authenticate());
        }
      }
      // CASO 2: App se abrió desde cerrada (cold start)
      else {
        this.checkAndProcessSharedData();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.platform.ready();

    try {
      const style = this.platform.is('ios') ? Style.Dark : Style.Light;
      await StatusBar.setStyle({ style });
    } catch (e) {
      console.warn('StatusBar config failed', e);
    }
  }

  /**
   * Verifica si hay datos compartidos y navega a la pantalla correspondiente
   */
  private checkAndProcessSharedData(): void {
    this.iosShareExtensionDataPluginService
      .read()
      .then(iosShareData => {
        if (iosShareData?.length > 0) {
          this.store.dispatch(
            ShareFileWithSdActions.sharedFilesReceived({
              shareFiles: [
                ...(iosShareData?.map(sharedFile => ({
                  fileName: sharedFile.fileName,
                  fileSize: sharedFile.fileSize,
                  uri: sharedFile.url,
                })) ?? []),
              ],
            })
          );
        }

        if (this.store.selectSignal(sharedFilesAllSelector)()?.length > 0) {
          this.ngZone.run(() => {
            this.router.navigateByUrl(
              `/${CoreRoutes.root}/${FilesRoutes.shareWithSd}`
            );
          });
        }
      })
      .catch(error => {
        console.error('[MobileApp] Error reading shared data:', error);
      });
  }
}
